import { create } from 'zustand';
import type { PortfolioSummary, Position } from '@/types';

interface PortfolioState {
  positions: Position[];
  portfolioSummary: PortfolioSummary | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setPositions: (positions: Position[]) => void;
  setPortfolioSummary: (summary: PortfolioSummary) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

// Computed selectors
export const selectTotalValue = (state: PortfolioState): number => {
  return state.portfolioSummary?.total_value ?? 0;
};

export const selectTotalPnl = (state: PortfolioState): number => {
  return state.portfolioSummary?.total_pnl ?? 0;
};

export const selectTotalPnlPercent = (state: PortfolioState): number => {
  return state.portfolioSummary?.total_pnl_percent ?? 0;
};

export const selectDayPnl = (state: PortfolioState): number => {
  return state.portfolioSummary?.day_pnl ?? 0;
};

export const selectDayPnlPercent = (state: PortfolioState): number => {
  return state.portfolioSummary?.day_pnl_percent ?? 0;
};

export const selectCashBalance = (state: PortfolioState): number => {
  return state.portfolioSummary?.cash_balance ?? 0;
};

export const selectPositionsValue = (state: PortfolioState): number => {
  return state.portfolioSummary?.positions_value ?? 0;
};

export const selectPositionCount = (state: PortfolioState): number => {
  return state.positions.length;
};

const initialState = {
  positions: [],
  portfolioSummary: null,
  isLoading: false,
  error: null,
};

export const usePortfolioStore = create<PortfolioState>()((set) => ({
  ...initialState,

  setPositions: (positions: Position[]) =>
    set({
      positions,
      error: null,
    }),

  setPortfolioSummary: (summary: PortfolioSummary) =>
    set({
      portfolioSummary: summary,
      error: null,
    }),

  setLoading: (isLoading: boolean) =>
    set({
      isLoading,
    }),

  setError: (error: string | null) =>
    set({
      error,
      isLoading: false,
    }),

  clearError: () =>
    set({
      error: null,
    }),

  reset: () => set(initialState),
}));
